import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BaseDestroyable } from '@core/base-destroyable';
import { KnownComponentsNames } from '@core/components-names';
import { PlatformCommunicationEvents, ImageHubAppParameters } from '@shared/generalInterfaces';
import { SnapshotResults } from '@itero/itero-client-communication';
import { PostMessageService } from '@shared/post-message.service';
import { CaptureSupportService } from 'app/doctors/iosimPlus/services/capture-support.service';
import { PlatformCommunicationService } from 'app/platform/services/platform-communication/platform-communication.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { StickyHeaderService } from 'app/services/stickyHeaderService/stickyHeader.service';
import { SpinnerService } from '@core/spinner/spinner.service';
import { switchMap, takeUntil, tap, take, filter, distinctUntilChanged, Subscription, combineLatest, race, timer, map } from 'rxjs';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { FeatureToggle } from '@shared/enums';

@Component({
	selector: 'eup-screenshot-manager',
	templateUrl: './screenshot-manager.component.html',
	styleUrl: './screenshot-manager.component.scss',
})
export class ScreenshotManagerComponent extends BaseDestroyable implements OnInit, OnDestroy {
	private readonly animationTimeWithoutThumbnail = 100;
	private readonly animationTimeWithThumbnail = 5000;
	private readonly screenshotTimeoutMs = 10000;
	private screenshotsSpinnerEnabled = false;

	@Input() isDrawingToolAllowed: boolean;

	capturedImageData: object;
	snapshotsData: SnapshotResults['data']['snapshots'] | null = null;
	showDrawingTool: boolean;
	showFlashAnimation: boolean = false;
	captureAnimationTimeOut: number;
	screenshotSrc: string = '';
	orderId: string;
	page: KnownComponentsNames;

	private abortSaveScreenshot = false;
	initSub: Subscription;

	constructor(
		private platformCommunicationService: PlatformCommunicationService,
		private stickyHeaderService: StickyHeaderService,
		private captureSupportService: CaptureSupportService,
		private shellContextService: ShellContextService,
		private router: Router,
		private postMessageService: PostMessageService,
		private applicationNavigationService: ApplicationNavigationService,
		private spinnerService: SpinnerService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService
	) {
		super();
	}

	private isScreenshotsSpinnerEnabled(): boolean {
		return this.screenshotsSpinnerEnabled;
	}

	ngOnInit(): void {
		this.featuresToggleSettingsService
			.getFeaturesToggleSettings()
			.pipe(take(1), takeUntil(this.componentAlive$))
			.subscribe(() => {
				this.screenshotsSpinnerEnabled = this.featuresToggleSettingsService.isVisibleFeature(
					FeatureToggle.EnableScreenshotsSpinner,
					false
				);
			});

		this.showDrawingTool = false;
		this.initSub = this.shellContextService
			.getOrder()
			.pipe(
				takeUntil(this.componentAlive$),
				filter((order) => !!order),
				tap((order) => (this.orderId = order.id?.toString())),
				switchMap((_) => {
					return this.stickyHeaderService.screenshotClicked$;
				}),
				filter((result) => !!result),
				switchMap(() => {
					this.stickyHeaderService.screenshotsResult$.next(null);
					this.stickyHeaderService.snapshotResults$.next(null);
					this.publishScreenshotClickEvent();

					if (this.isScreenshotsSpinnerEnabled() && this.page !== KnownComponentsNames.AxiWrapper) {
						this.spinnerService.start();
					}

					const result$ = combineLatest([
						this.stickyHeaderService.snapshotResults$,
						this.stickyHeaderService.screenshotsResult$
					]).pipe(
						distinctUntilChanged(),
						map(([snapshotResults, screenshotsResults]) => {
							if (snapshotResults) {
								return { type: 'snapshot', data: snapshotResults };
							}
							if (screenshotsResults) {
								return { type: 'screenshot', data: screenshotsResults };
							}
							if (screenshotsResults === false) {
								return { type: 'screenshot-failed', data: null };
							}
							return null;
						}),
						filter((result) => result !== null),
						take(1)
					);

					const timeout$ = timer(this.screenshotTimeoutMs).pipe(
						map(() => ({ type: 'timeout', data: null }))
					);

					return race(result$, timeout$);
				}),

				tap((result) => {
					if (result.type === 'snapshot') {
						this.prepareSaveSnapshot(result.data);
					} else if (result.type === 'screenshot-failed' || result.type === 'timeout') {
						if (this.isScreenshotsSpinnerEnabled()) {
							this.spinnerService.stop();
						}
					} else {
						const filteredData = this.captureSupportService.removeScreenShotObjEmptyKeys(result.data);
						if (this.captureSupportService.isScreenShotsDataValid(filteredData)) {
							this.prepareSaveScreenshot(filteredData);
						} else {
							if (this.isScreenshotsSpinnerEnabled()) {
								this.spinnerService.stop();
							}
							this.captureSupportService.showError(result.data, this.page);
						}
					}
				})
			)
			.subscribe();
	}

	override ngOnDestroy(): void {
		this.initSub.unsubscribe();
		this.stickyHeaderService.resetSubjects();

		if(this.captureAnimationTimeOut) {
			clearTimeout(this.captureAnimationTimeOut);
			this.captureAnimationTimeOut = null;
			this.completeCaptureAnimation();
		}
		
		super.ngOnDestroy();
	}

	onOpenDrawingTool() {
		this.abortSaveScreenshot = true;
		this.showFlashAnimation = false;
		if (this.isScreenshotsSpinnerEnabled()) {
			this.spinnerService.stop();
		}

		if (this.stickyHeaderService.showImageHub || this.stickyHeaderService.showAnnotationTool) {
			// Clear timeout to prevent auto-save when user opens drawing tool
			if (this.captureAnimationTimeOut) {
				clearTimeout(this.captureAnimationTimeOut);
				this.captureAnimationTimeOut = null;
			}
			const snapshotResults = this.stickyHeaderService.snapshotResults$.value;
			const appParameters: ImageHubAppParameters = {};

			if (snapshotResults) {
				appParameters.snapshotResults = snapshotResults;
			} else {
				appParameters.newImages = this.captureSupportService.getCapturedImageDataForPatientGallery(this.capturedImageData);
			}

			this.applicationNavigationService.navigateToStandardRemoteApplication(KnownComponentsNames.ImageHub, {
				appParameters,
			});
			this.stickyHeaderService.snapshotResults$.next(null);
	
			this.capturedImageData = null;
			this.snapshotsData = null;
		} else {
			this.showDrawingTool = true;
		}
	}

	onImageLoaded(capturedImage: object) {
		this.abortSaveScreenshot = false;
		this.saveScreenshot(capturedImage);
		this.showDrawingTool = false;
		this.capturedImageData = null;
	}

	private publishScreenshotClickEvent() {
		this.setPage();
		this.showFlashAnimation = false;
		clearTimeout(this.captureAnimationTimeOut);
		switch (this.page) {
			case KnownComponentsNames.AohsComponent:
			case KnownComponentsNames.WebViewer:
			case KnownComponentsNames.AxiWrapper:
				this.platformCommunicationService.publishEvent(PlatformCommunicationEvents.ScreenshotClicked, ['*'], undefined);
				break;
			case KnownComponentsNames.IosimPlus:
				{
					// const iframe = this.iosimPlusIFrame.nativeElement;
					// const takeScreenshotCommand = {
					// 	orderId: this.orderId,
					// 	key: PostMessageKeyType.TakeScreenshotResults,
					// };
					// this.postMessageService.sendToIFrame(iframe, takeScreenshotCommand);
				}
				break;
			default:
				break;
		}
	}

	private setPage() {
		if (this.router.url.includes(KnownComponentsNames.AohsComponent)) {
			this.page = KnownComponentsNames.AohsComponent;
		} else if (this.router.url.includes('viewer')) {
			this.page = KnownComponentsNames.WebViewer;
		} else if (this.router.url.includes(KnownComponentsNames.IosimPlus)) {
			this.page = KnownComponentsNames.IosimPlus;
		} else if (this.router.url.includes(KnownComponentsNames.AxiWrapper)) {
			this.page = KnownComponentsNames.AxiWrapper;
		}
	}

	private prepareSaveScreenshot(data: any) {
		this.abortSaveScreenshot = false;
		this.createScreenshotSrc(data);
		this.showFlashAnimation = true;
		this.capturedImageData = data;
		this.captureAnimationTimeOut = window.setTimeout(() => {
			this.completeCaptureAnimation();
		}, this.getCaptureAnimationTime());
	}

	private saveScreenshot(data) {
		// Additional safety check to prevent saving when aborted
		if (this.abortSaveScreenshot || !data) {
			return;
		}

		this.captureSupportService
			.saveScreenShotNotIosim(data, this.orderId)
			.pipe(
				takeUntil(this.componentAlive$),
				take(1)
			)
			.subscribe();

		this.stickyHeaderService.callNext('downloadScreenshotsEnabled$', true);
	}

	private getCaptureAnimationTime() {
		return this.isDrawingToolAllowed ? this.animationTimeWithThumbnail : this.animationTimeWithoutThumbnail;
	}

	private createScreenshotSrc(data) {
		const img = new Image();
		img.onload = () => {
			const newImage = this.getThumbnail(img, 0.15);
			this.screenshotSrc = newImage.toDataURL('image/jpeg', 1.0);
		};

		const fullScreenDataKey = this.captureSupportService.getFullScreenKey(data);
		const fullScreenDataUrl = data[fullScreenDataKey];

		img.src = fullScreenDataUrl;
	}

	private createSnapshotSrc(data: SnapshotResults['data']['snapshots']) {
		const img = new Image();
		img.onload = () => {
			const newImage = this.getThumbnail(img, 0.15);
			this.screenshotSrc = newImage.toDataURL('image/jpeg', 1.0);
		};
		const fullScreenDataUrl = data[0].data;
		img.src = fullScreenDataUrl;
	}

	private getThumbnail(original, scale) {
		const canvas = document.createElement('canvas');
		canvas.width = original.width * scale;
		canvas.height = original.height * scale;
		canvas.getContext('2d').drawImage(original, 0, 0, canvas.width, canvas.height);

		return canvas;
	}

	private completeCaptureAnimation() {
		if (this.isScreenshotsSpinnerEnabled()) {
			this.spinnerService.stop();
		}

		if (!this.abortSaveScreenshot) {
			if (this.snapshotsData) {
				this.saveSnapshot(this.snapshotsData);
				this.stickyHeaderService.snapshotResults$.next(null);
			} else if (this.capturedImageData) {
				this.saveScreenshot(this.capturedImageData);
				this.stickyHeaderService.screenshotsResult$.next(null);
			}
		}
		this.captureAnimationTimeOut = null;
		this.showFlashAnimation = false;
	}

	private saveSnapshot(data: SnapshotResults['data']['snapshots']) {
		// Additional safety check to prevent saving when aborted
		if (this.abortSaveScreenshot || !data) {
			return;
		}

		this.captureSupportService
			.saveSnapshots(data, this.orderId)
			.pipe(
				takeUntil(this.componentAlive$),
				take(1)
			)
			.subscribe();
		this.stickyHeaderService.callNext('downloadScreenshotsEnabled$', true);
	}

	private prepareSaveSnapshot(data: SnapshotResults['data']['snapshots']) {
		this.abortSaveScreenshot = false;
		this.snapshotsData = data;
		this.createSnapshotSrc(data);
		this.showFlashAnimation = true;
		this.captureAnimationTimeOut = window.setTimeout(() => {
			this.completeCaptureAnimation();
		}, this.getCaptureAnimationTime());
	}
}
